import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { z } from 'zod';
import { ORGANISATION_ROLES, createOrganisation } from '@mkcore/crm/schema';
import { Badge, Button, Checkbox, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, EmptyState, ErrorState, Field, Input, NativeSelect, PageHeader, Skeleton } from '@mkcore/ui';
import { newIdempotencyKey } from '@/lib/api';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { organisationsQuery } from '@/features/crm/queries';
import { RoleBadges } from '@/features/crm/labels';
import { clientCode, organisationErrors } from '@/features/crm/organisation-code';
import { DataTable, type ColumnDef } from '@/components/data-table';
import { formatDate } from '@/lib/format';
import { multi } from '@/lib/view';

const search = z.object({ q: z.string().optional(), role: z.enum(ORGANISATION_ROLES).optional(), archived: z.boolean().optional() });

export const Route = createFileRoute('/_app/org/')({ validateSearch: search, component: Organisations });

/** Clients, prospects, suppliers and partners (CO-0161). */
function Organisations() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const message = useErrorMessage();
  const canCreate = useCan('crm:create');
  const [open, setOpen] = useState(false);
  const q = useInfiniteQuery(organisationsQuery({ q: s.q || undefined, role: s.role ? [s.role] : undefined, includeArchived: s.archived || undefined }));
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const set = (patch: Partial<z.infer<typeof search>>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const columns = useMemo<ColumnDef<(typeof rows)[number]>[]>(
    () => [
      {
        id: 'name',
        label: t('common.name'),
        value: (o) => o.name,
        cell: (o) => (
          <>
            <Link to="/org/$id" params={{ id: o.id }} className="font-bold hover:underline">
              {o.name}
            </Link>
            {o.archivedAt && (
              <Badge tone="neutral" className="ml-2">
                {t('org.archived')}
              </Badge>
            )}
          </>
        ),
      },
      { id: 'code', label: t('org.fields.code'), value: (o) => o.code, cell: (o) => <span className="tabular text-fg-2">{o.code ?? '–'}</span> },
      {
        id: 'roles',
        label: t('org.fields.roles'),
        type: 'enum',
        options: ORGANISATION_ROLES.map((r) => ({ value: r, label: t(`org.role.${r}`) })),
        groupable: true,
        value: (o) => multi(o.roles),
        cell: (o) => <RoleBadges roles={o.roles} />,
      },
      { id: 'city', label: t('org.fields.city'), groupable: true, value: (o) => o.city, cell: (o) => <span className="text-fg-2">{o.city ?? '–'}</span>, className: 'hidden md:table-cell' },
      { id: 'country', label: t('org.fields.country'), groupable: true, hidden: true, value: (o) => o.country, cell: (o) => o.country },
      { id: 'emailDomains', label: t('org.fields.emailDomains'), value: (o) => o.emailDomains.join(', ') || null, cell: (o) => <span className="text-fg-2">{o.emailDomains.join(', ') || '–'}</span>, className: 'hidden lg:table-cell' },
      { id: 'paymentTermDays', label: t('org.fields.paymentTermDays'), type: 'number', hidden: true, value: (o) => o.paymentTermDays, cell: (o) => o.paymentTermDays ?? '–', className: 'text-right tabular' },
      { id: 'createdAt', label: t('common.created'), type: 'date', hidden: true, value: (o) => o.createdAt, cell: (o) => formatDate(o.createdAt) },
      { id: 'contacts', label: t('org.contacts.title'), type: 'number', value: (o) => o.contactCount, cell: (o) => o.contactCount, className: 'text-right tabular' },
    ],
    [t],
  );

  return (
    <>
      <PageHeader
        title={t('org.title')}
        description={t('org.description')}
        actions={
          canCreate && (
            <Button onClick={() => setOpen(true)}>
              <Plus /> {t('org.new')}
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('common.search')} className="w-72">
          {(p) => <Input {...p} value={s.q ?? ''} placeholder={t('org.searchHint')} onChange={(e) => set({ q: e.target.value || undefined })} />}
        </Field>
        <Field label={t('org.fields.roles')} className="w-44">
          {(p) => (
            <NativeSelect {...p} value={s.role ?? ''} onChange={(e) => set({ role: (e.target.value || undefined) as never })}>
              <option value="">{t('common.all')}</option>
              {ORGANISATION_ROLES.map((r) => (
                <option key={r} value={r}>
                  {t(`org.role.${r}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <Checkbox checked={!!s.archived} onCheckedChange={(v) => set({ archived: v === true || undefined })} /> {t('org.includeArchived')}
        </label>
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-64" />
      ) : !rows.length ? (
        <EmptyState title={t('org.empty')} description={canCreate ? t('org.emptyHint') : undefined} />
      ) : (
        <DataTable listKey="org.organisation.list" columns={columns} rows={rows} rowKey={(o) => o.id} onOpen={(o) => void navigate({ to: '/org/$id', params: { id: o.id } })} partial={q.hasNextPage} onNeedAll={() => q.hasNextPage && !q.isFetchingNextPage && void q.fetchNextPage()} />
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
      <NewOrganisation open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function NewOrganisation({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const message = useErrorMessage();
  const [key] = useState(newIdempotencyKey);
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [roles, setRoles] = useState<string[]>(['client']);
  const [domains, setDomains] = useState('');
  const [city, setCity] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const create = useActionMutation(createOrganisation, {
    invalidate: [['org']],
    silentErrors: true,
    onSuccess: (o) => {
      onClose();
      void navigate({ to: '/org/$id', params: { id: o.id } });
    },
  });
  const submit = () =>
    create.mutate(
      {
        input: {
          name,
          code: code || null,
          roles: roles as never,
          emailDomains: domains
            .split(/[,\s]+/)
            .map((d) => d.trim())
            .filter(Boolean),
          city: city || null,
        },
        key,
      },
      { onError: (e) => setErrors(organisationErrors(e, message, t)) },
    );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('org.new')}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label={t('common.name')} error={errors.name}>
            {(p) => <Input {...p} autoFocus value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label={t('org.fields.code')} hint={t('org.fields.codeHint')} error={errors.code}>
            {(p) => <Input {...p} value={code} placeholder="ACME" maxLength={10} onChange={(e) => setCode(clientCode(e.target.value))} />}
          </Field>
          <div>
            <div className="mb-1 text-sm font-bold">{t('org.fields.roles')}</div>
            <div className="flex flex-wrap gap-4">
              {ORGANISATION_ROLES.map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={roles.includes(r)} onCheckedChange={(v) => setRoles(v === true ? [...roles, r] : roles.filter((x) => x !== r))} /> {t(`org.role.${r}`)}
                </label>
              ))}
            </div>
            {errors.roles && <p className="mt-1 text-xs text-danger">{errors.roles}</p>}
          </div>
          <Field label={t('org.fields.emailDomains')} hint={t('org.fields.emailDomainsHint')} error={errors.emailDomains}>
            {(p) => <Input {...p} value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="acme.example, acme-group.example" />}
          </Field>
          <Field label={t('org.fields.city')}>{(p) => <Input {...p} value={city} onChange={(e) => setCity(e.target.value)} />}</Field>
          {errors._ && (
            <p role="alert" className="text-sm text-danger">
              {errors._}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button loading={create.isPending} onClick={submit}>
            {t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

